import React, { useState, useEffect } from 'react';
import { X, Shield, ShieldCheck, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import { Admin, AdminUpdateData } from '../types';

interface EditAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin: Admin | null;
  currentUserRole: 'super_admin' | 'admin' | 'member';
  currentUserId: number;
}

const EditAdminModal: React.FC<EditAdminModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  admin,
  currentUserRole,
  currentUserId,
}) => {
  const [formData, setFormData] = useState<AdminUpdateData>({
    email: '',
    full_name: '',
    role: 'admin',
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when admin changes
  useEffect(() => {
    if (admin) {
      setFormData({
        email: admin.email,
        full_name: admin.full_name,
        role: admin.role,
        is_active: admin.is_active,
      });
    }
  }, [admin]);

  const canEditRole = currentUserRole === 'super_admin';
  const isEditingSelf = admin?.id === currentUserId;
  const canEditOther = currentUserRole === 'super_admin' || isEditingSelf;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Full name validation
    if (!formData.full_name) {
      newErrors.full_name = 'Full name is required';
    } else if (formData.full_name.length < 2) {
      newErrors.full_name = 'Full name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!admin) return;

    if (!validateForm()) {
      return;
    }

    // Check permissions
    if (!canEditOther) {
      toast.error('You can only edit your own profile');
      return;
    }

    setIsSubmitting(true);

    try {
      // Only send changed fields
      const updatePayload: AdminUpdateData = {};
      if (formData.email !== admin.email) updatePayload.email = formData.email;
      if (formData.full_name !== admin.full_name) updatePayload.full_name = formData.full_name;
      if (canEditRole && formData.role !== admin.role) updatePayload.role = formData.role;
      if (canEditRole && formData.is_active !== admin.is_active) updatePayload.is_active = formData.is_active;

      if (Object.keys(updatePayload).length === 0) {
        toast.info('No changes to save');
        handleClose();
        return;
      }

      const response = await axios.put(`/admins/${admin.id}`, updatePayload);

      toast.success(response.data.message || 'Admin updated successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error updating admin:', error);

      if (error.response?.data?.error?.message) {
        toast.error(error.response.data.error.message);
      } else if (error.response?.data?.errors) {
        // Validation errors
        const validationErrors: Record<string, string> = {};
        error.response.data.errors.forEach((err: any) => {
          validationErrors[err.field] = err.message;
        });
        setErrors(validationErrors);
      } else {
        toast.error('Failed to update admin user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen || !admin) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Save className="w-5 h-5 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Edit Admin User</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Permission Warning */}
        {!canEditOther && (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              You can only edit your own profile
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Full Name */}
          <div>
            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="full_name"
              value={formData.full_name}
              onChange={(e) => {
                setFormData({ ...formData, full_name: e.target.value });
                setErrors({ ...errors, full_name: '' });
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.full_name ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isSubmitting || !canEditOther}
            />
            {errors.full_name && (
              <p className="text-sm text-red-500 mt-1">{errors.full_name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setErrors({ ...errors, email: '' });
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isSubmitting || !canEditOther}
            />
            {errors.email && (
              <p className="text-sm text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Role - Only for super admin */}
          {canEditRole && (
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={formData.role === 'admin'}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' })}
                    className="text-primary-600 focus:ring-primary-500"
                    disabled={isSubmitting}
                  />
                  <Shield className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Admin</div>
                    <div className="text-sm text-gray-500">Standard admin privileges</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="role"
                    value="super_admin"
                    checked={formData.role === 'super_admin'}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as 'super_admin' })}
                    className="text-primary-600 focus:ring-primary-500"
                    disabled={isSubmitting}
                  />
                  <ShieldCheck className="w-5 h-5 text-primary-600" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Super Admin</div>
                    <div className="text-sm text-gray-500">Full system access</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Active Status - Only for super admin */}
          {canEditRole && (
            <div>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500 rounded"
                  disabled={isSubmitting || isEditingSelf}
                />
                <span className="text-sm font-medium text-gray-700">
                  Active Account
                </span>
              </label>
              {isEditingSelf && (
                <p className="text-xs text-gray-500 mt-1 ml-7">
                  You cannot deactivate your own account
                </p>
              )}
            </div>
          )}

          {/* Current Role Display - For non-super admins */}
          {!canEditRole && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Role
              </label>
              <div className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                {admin.role === 'super_admin' ? (
                  <>
                    <ShieldCheck className="w-5 h-5 text-primary-600" />
                    <span className="font-medium text-gray-900">Super Admin</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Admin</span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Only super admins can change roles
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !canEditOther}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdminModal;
